/**
 * @file word-dialog-bridge.service.ts
 * @description Bridge for completing auth when kirigami-app is loaded inside a
 * Microsoft Office Dialog opened by the Word Add-in task pane via
 * `Office.context.ui.displayDialogAsync`.
 *
 * The Word task pane opens `/login?host=word` in a dialog. This service:
 *   1. Detects the `?host=word` marker and remembers it across the Cognito
 *      OAuth round-trip by persisting a flag in localStorage (same origin as
 *      the dialog, so the value survives redirecting away to Cognito and back
 *      to `/auth-callback.html`).
 *   2. Exposes `isInWordDialog()` so auth-completion call sites can choose
 *      between navigating inside the dialog (wrong) and messaging the parent
 *      task pane (right).
 *   3. Lazy-loads Office.js on demand and calls
 *      `Office.context.ui.messageParent(...)` with the session payload so the
 *      task pane can close the dialog and pick up the newly minted session.
 *
 * In normal (non-Word) browser navigation this service is a strict no-op.
 *
 * Platform boundary: this service lives in the app rather than in
 * `libs/platform/office` because it is a narrow dialog-side helper used by
 * the web app when a dialog happens to point at its origin — not Word
 * runtime code. Office.js is loaded lazily and only when needed.
 *
 * Legacy reference: kirigami-analysis auth-callback.html (messageParent call)
 * and office.service.ts (dialog open + DialogMessageReceived handling, which
 * is the peer side that lives in apps/word-addin).
 */
import { Injectable } from '@angular/core';

/** Query param the task pane sets when launching the login dialog. */
const HOST_PARAM = 'host';
const HOST_WORD_VALUE = 'word';

/** localStorage flag persisted across the OAuth round-trip. */
const WORD_DIALOG_FLAG_KEY = 'word_dialog_origin';

/** URL of the Office.js runtime loaded on demand inside the dialog. */
const OFFICE_JS_URL = 'https://appsforoffice.microsoft.com/lib/1/hosted/office.js';

/** Payload sent back to the task pane on successful auth. */
export interface WordDialogLoginSuccessPayload {
  accessToken: string;
  email: string;
  displayName: string;
}

/** Shape of the message posted via `messageParent` (kept stable for the addin side). */
interface WordDialogMessageEnvelope {
  status: 'logging-success';
  atk: string;
  em: string;
  name: string;
}

interface OfficeUi {
  messageParent(message: string, options?: { targetOrigin?: string }): void;
}
interface OfficeContext {
  ui?: OfficeUi;
}
interface OfficeGlobal {
  onReady(callback?: (info: { host: unknown; platform: unknown }) => void): Promise<unknown>;
  context?: OfficeContext;
}

@Injectable({ providedIn: 'root' })
export class WordDialogBridgeService {
  private officeLoadPromise: Promise<OfficeGlobal> | null = null;

  /**
   * Inspects the current URL for `?host=word` and, when present, persists
   * the flag so it survives the Cognito OAuth round-trip. Safe to call on
   * every boot; no-op in SSR or when the marker is absent.
   */
  detectAndRemember(): void {
    if (typeof window === 'undefined') return;
    try {
      const params = new URL(window.location.href).searchParams;
      if (params.get(HOST_PARAM) === HOST_WORD_VALUE) {
        window.localStorage.setItem(WORD_DIALOG_FLAG_KEY, '1');
      }
    } catch {
      /* malformed URL or storage blocked — ignore */
    }
  }

  /**
   * True when the current window was opened from the Word add-in task pane.
   * Reads the flag set by `detectAndRemember()`; also accepts a fresh
   * `?host=word` in case the check runs before detection.
   */
  isInWordDialog(): boolean {
    if (typeof window === 'undefined') return false;
    try {
      if (window.localStorage.getItem(WORD_DIALOG_FLAG_KEY) === '1') return true;
      const params = new URL(window.location.href).searchParams;
      return params.get(HOST_PARAM) === HOST_WORD_VALUE;
    } catch {
      return false;
    }
  }

  /** Clears the dialog marker. Called after a successful handover. */
  clear(): void {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.removeItem(WORD_DIALOG_FLAG_KEY);
    } catch {
      /* storage blocked — ignore */
    }
  }

  /**
   * Sends the login-success envelope to the parent task pane.
   * Lazy-loads Office.js and waits for `Office.onReady`. Throws if the bridge
   * is invoked outside a real Office dialog (caller is expected to fall back
   * to normal navigation after catching).
   */
  async notifyLoginSuccess(payload: WordDialogLoginSuccessPayload): Promise<void> {
    const office = await this.loadOfficeJs();
    await office.onReady();

    const ui = office.context?.ui;
    if (!ui || typeof ui.messageParent !== 'function') {
      throw new Error(
        'Office dialog UI is not available because this is not running inside a dialog.',
      );
    }

    const envelope: WordDialogMessageEnvelope = {
      status: 'logging-success',
      atk: payload.accessToken,
      em: payload.email,
      name: payload.displayName,
    };
    ui.messageParent(JSON.stringify(envelope));
    this.clear();
  }

  /**
   * Lazy-loads the Office.js runtime by injecting a `<script>` tag. Caches
   * the promise so repeated calls reuse the in-flight load. Rejects if the
   * script fails to load or `window.Office` is still missing afterwards.
   */
  private loadOfficeJs(): Promise<OfficeGlobal> {
    if (typeof window === 'undefined') {
      return Promise.reject(new Error('Office.js requires a browser window.'));
    }
    const existing = (window as unknown as { Office?: OfficeGlobal }).Office;
    if (existing) return Promise.resolve(existing);

    if (this.officeLoadPromise) return this.officeLoadPromise;

    this.officeLoadPromise = new Promise<OfficeGlobal>((resolve, reject) => {
      const script = window.document.createElement('script');
      script.src = OFFICE_JS_URL;
      script.async = true;
      script.onload = () => {
        const loaded = (window as unknown as { Office?: OfficeGlobal }).Office;
        if (loaded) resolve(loaded);
        else reject(new Error('Office.js loaded but `window.Office` is undefined.'));
      };
      script.onerror = () => reject(new Error('Failed to load Office.js from CDN.'));
      window.document.head.appendChild(script);
    });

    return this.officeLoadPromise;
  }
}
